import React, { useEffect, useRef } from "react";
import "@/marketing/components/full-site/AnimatedHeader.css";
import { HERO_HEADLINE } from "@/shared/components/full-site/constants/heroConstants";

// MiDash brand colors - theme accent variations
const PARTICLE_SHADES = [
  "#6dd8d0", // --color-accent-light
  "#5bc9c1", // --color-accent
  "#4db8b0", // --color-accent-deep
  "#3f9da1", // --color-accent-dark
  "#00d4aa", // --color-accent-pure
];

const WORDS = [
  "Scans",
  "Analyzes",
  "Tests",
  "Executes",
  "Deploys",
  "Remembers",
];

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));
const TAU = Math.PI * 2;

const hexToRgb = (hex: string) => {
  const normalized = (hex || "").replace("#", "").trim();
  if (normalized.length !== 6) return { r: 49, g: 180, b: 171 };
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);

type Phase = "float" | "forming" | "display" | "morph";
type ParticleMode = "float" | "toText" | "text" | "morph";

const sampleFieldPoint = (width: number, height: number) => {
  const cx = width * 0.5;
  const cy = height * 0.5;
  const rx = width * 0.58;
  const ry = height * 0.42;

  const theta = Math.random() * TAU;
  // Spread particles away from the center so the rotating word reads cleaner (less “dust cloud”).
  const radius = Math.sqrt(Math.random());
  return {
    x: cx + Math.cos(theta) * rx * radius,
    y: cy + Math.sin(theta) * ry * radius,
  };
};

const vignetteAlpha = (x: number, y: number, width: number, height: number) => {
  const nx = (x - width * 0.5) / Math.max(1, width * 0.5);
  const ny = (y - height * 0.5) / Math.max(1, height * 0.5);
  const r = Math.sqrt(nx * nx + ny * ny);
  const t = clamp((r - 0.58) / 0.48, 0, 1);
  const fade = 1 - t;
  return fade * fade;
};

interface TextPoint {
  x: number;
  y: number;
  size: number;
  alpha: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  baseSize: number;
  size: number;
  z: number;
  color: string;
  r: number;
  g: number;
  b: number;
  darkR: number;
  darkG: number;
  darkB: number;
  baseAlpha: number;
  wobblePhase: number;
  wobbleSpeed: number;
  mode: ParticleMode;
  progress: number;
  delay: number;
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  targetSize: number;
  targetAlpha: number;
}

const AnimatedHeaderMorph: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<number | null>(null);

  const particlesRef = useRef<Particle[]>([]);
  const textParticleIndicesRef = useRef<number[] | null>(null);
  const dprRef = useRef(1);
  const dimensionsRef = useRef({ width: 0, height: 90 }); // Reduced by 25%
  const contentRectRef = useRef({ width: 480, height: 90, anchorX: 0, anchorY: 0 }); // Reduced by 25%

  const phaseRef = useRef<Phase>("float");
  const phaseStartRef = useRef(performance.now());
  const lastFrameRef = useRef(performance.now());
  const wordIndexRef = useRef(0);
  const shownWordRef = useRef(WORDS[0]);
  const incomingWordRef = useRef<string | null>(null);
  const fontSizeRef = useRef(48); // Reduced by 25% from 64
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitializedRef = useRef(false);

  const createParticle = (width: number, height: number): Particle => {
    const color = PARTICLE_SHADES[Math.floor(Math.random() * PARTICLE_SHADES.length)];
    const { r, g, b } = hexToRgb(color);
    const angle = Math.random() * TAU;
    const isMobile = width < 520;
    const speed = randomBetween(isMobile ? 5 : 7, isMobile ? 16 : 20);
    // Smaller beads for a tighter, more premium look.
    const baseSize = randomBetween(0.65, 1.55);
    const { x, y } = sampleFieldPoint(width, height);
    const z = randomBetween(0.12, 1);
    return {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      baseSize,
      size: baseSize,
      z,
      color,
      r,
      g,
      b,
      darkR: Math.max(0, r - 70),
      darkG: Math.max(0, g - 70),
      darkB: Math.max(0, b - 70),
      baseAlpha: randomBetween(isMobile ? 0.03 : 0.04, isMobile ? 0.085 : 0.11),
      wobblePhase: Math.random() * TAU,
      wobbleSpeed: randomBetween(0.35, 0.95),
      mode: "float",
      progress: 0,
      delay: randomBetween(0, 0.1),
      startX: 0,
      startY: 0,
      targetX: 0,
      targetY: 0,
      targetSize: baseSize,
      targetAlpha: 1,
    };
  };

  const initField = () => {
    const { width, height } = dimensionsRef.current;
    const isMobile = width < 520;
    const count = clamp(
      Math.floor(width * (isMobile ? 5.6 : 8.0)),
      isMobile ? 1400 : 2400,
      isMobile ? 3000 : 5200
    );
    particlesRef.current = Array.from({ length: count }, () => createParticle(width, height));
    textParticleIndicesRef.current = null;
  };

  const getTextPoints = (text: string, fontSize: number): TextPoint[] => {
    const { width: boxWidth, height: boxHeight, anchorX, anchorY } = contentRectRef.current;
    const dpr = dprRef.current || 1;

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = Math.max(1, Math.floor(boxWidth * dpr));
    tempCanvas.height = Math.max(1, Math.floor(boxHeight * dpr));
    const ctx = tempCanvas.getContext("2d");
    if (!ctx) return [];

    ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
    ctx.fillStyle = "#31B4AB";
    ctx.font = `600 ${Math.floor(fontSize * dpr)}px Montserrat, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, tempCanvas.width / 2, tempCanvas.height / 2);

    const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    const points: TextPoint[] = [];

    const baseGap = boxWidth >= 760 ? 1 : dpr >= 1.5 ? 1 : 2;
    const gap = Math.max(1, Math.round(baseGap * dpr));
    const jitter = (gap / dpr) * (boxWidth < 520 ? 0.35 : 0.55);

    const originX = anchorX - boxWidth / 2;
    const originY = anchorY - boxHeight / 2;

    for (let y = 0; y < tempCanvas.height; y += gap) {
      for (let x = 0; x < tempCanvas.width; x += gap) {
        const i = (y * tempCanvas.width + x) * 4;
        const a = imageData.data[i + 3] / 255;
        if (a > 0.06) {
          points.push({
            x: originX + x / dpr + (Math.random() - 0.5) * jitter,
            y: originY + y / dpr + (Math.random() - 0.5) * jitter,
            size: 1.15 + a * 1.65 + Math.random() * 0.25,
            alpha: a,
          });
        }
      }
    }

    const maxPoints = clamp(Math.floor(boxWidth * 9.5), 2200, 5200);
    if (points.length <= maxPoints) return points;

    const sampled = points.slice(0, maxPoints);
    for (let i = maxPoints; i < points.length; i++) {
      const j = Math.floor(Math.random() * (i + 1));
      if (j < maxPoints) sampled[j] = points[i];
    }
    return sampled;
  };

  const ensureTextParticleSet = (pointsLength: number) => {
    const particles = particlesRef.current;
    if (!particles.length) return;
    if (textParticleIndicesRef.current) return;

    const indices: number[] = [];
    for (let i = 0; i < particles.length; i++) indices.push(i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    const usedRatio = dimensionsRef.current.width < 520 ? 0.80 : 0.72;
    const used = Math.min(pointsLength, Math.floor(particles.length * usedRatio));
    textParticleIndicesRef.current = indices.slice(0, used);
  };

  const assignTargetsForWord = (word: string, mode: "toText" | "morph") => {
    const { width } = dimensionsRef.current;
    const { width: contentWidth } = contentRectRef.current;

    // Keep the rotating word secondary to the main headline + input (slightly smaller).
    // Reduced by 25% for better visual hierarchy
    const isMobile = (contentWidth || width) < 520;
    const fontSize = Math.min((contentWidth || width) * (isMobile ? 0.071 : 0.075), isMobile ? 47 : 56);
    fontSizeRef.current = fontSize;

    const points = getTextPoints(word, fontSize);
    const particles = particlesRef.current;
    if (!particles.length || !points.length) return;

    ensureTextParticleSet(points.length);
    const textIndices = textParticleIndicesRef.current;
    if (!textIndices?.length) return;
    const textIndexSet = new Set(textIndices);

    for (let i = 0; i < textIndices.length; i++) {
      const p = particles[textIndices[i]];
      const pt = points[i % points.length];
      p.mode = mode;
      p.progress = 0;
      p.delay = mode === "toText" ? randomBetween(0, 0.12) : randomBetween(0, 0.06);
      p.startX = p.x;
      p.startY = p.y;
      p.targetX = pt.x;
      p.targetY = pt.y;
      p.targetSize = pt.size;
      p.targetAlpha = clamp(0.35 + pt.alpha * 0.75, 0.35, 1);
      // Slight depth boost so text beads read as “3D spheres”.
      p.z = clamp(0.52 + pt.alpha * 0.52 + Math.random() * 0.16, 0.32, 1);
    }

    // Everything else stays as ambient float.
    for (let i = 0; i < particles.length; i++) {
      if (textIndexSet.has(i)) continue;
      const p = particles[i];
      p.mode = "float";
      p.z = randomBetween(0.12, 0.62);
      p.targetAlpha = width < 520 ? randomBetween(0.02, 0.08) : randomBetween(0.03, 0.10);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const canvasContainer = canvasContainerRef.current;
    if (!canvas || !container) return;

    const updateDimensions = (isInitial = false) => {
      // PERF: Batch all layout reads BEFORE any writes to avoid forced reflows
      const canvasRect = canvas.getBoundingClientRect();
      const containerRect = canvasContainer?.getBoundingClientRect();

      const dprMax = canvasRect.width < 520 ? 1.5 : 2;
      const dpr = clamp(window.devicePixelRatio || 1, 1, dprMax);
      dprRef.current = dpr;

      const oldWidth = dimensionsRef.current.width;
      const oldHeight = dimensionsRef.current.height;
      const newWidth = canvasRect.width;
      const newHeight = canvasRect.height;

      // Now perform all writes after reads are complete
      dimensionsRef.current = { width: newWidth, height: newHeight };

      canvas.width = Math.floor(newWidth * dpr);
      canvas.height = Math.floor(newHeight * dpr);

      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (containerRect) {
        const anchorX = containerRect.left + containerRect.width / 2 - canvasRect.left;
        const anchorY = containerRect.top + containerRect.height / 2 - canvasRect.top;
        contentRectRef.current = {
          width: containerRect.width,
          height: containerRect.height,
          anchorX,
          anchorY,
        };
      }

      // Only fully reinitialize on first load
      if (isInitial || !isInitializedRef.current) {
        initField();
        isInitializedRef.current = true;
        phaseRef.current = "float";
        phaseStartRef.current = performance.now();
        shownWordRef.current = WORDS[wordIndexRef.current];
        incomingWordRef.current = null;
      } else if (oldWidth > 0 && oldHeight > 0) {
        // On resize: scale particle positions proportionally
        const scaleX = newWidth / oldWidth;
        const scaleY = newHeight / oldHeight;
        particlesRef.current.forEach((p) => {
          p.x *= scaleX;
          p.y *= scaleY;
          if (p.targetX) p.targetX *= scaleX;
          if (p.targetY) p.targetY *= scaleY;
          if (p.startX) p.startX *= scaleX;
          if (p.startY) p.startY *= scaleY;
        });
        if (phaseRef.current === "display" || phaseRef.current === "forming") {
          assignWordTargets(shownWordRef.current);
        }
      }

      lastFrameRef.current = performance.now();
    };

    const handleResize = () => {
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => updateDimensions(false), 150);
    };

    updateDimensions(true);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const beadSpriteCache = new Map<string, HTMLCanvasElement>();
    let beadSpriteDpr = 0;

    const getBeadSprite = (radius: number, p: Particle) => {
      const dpr = dprRef.current || 1;
      if (!beadSpriteDpr || beadSpriteDpr !== dpr) {
        beadSpriteCache.clear();
        beadSpriteDpr = dpr;
      }

      const r = clamp(radius, 0.6, 3.2);
      const q = Math.round(r * 8) / 8; // 0.125px bins keeps cache small + crisp
      const key = `${dpr}|${q}|${p.r},${p.g},${p.b}`;
      const cached = beadSpriteCache.get(key);
      if (cached) return cached;

      const px = Math.max(2, Math.ceil(q * 2 * dpr));
      const c = document.createElement("canvas");
      c.width = px;
      c.height = px;
      const sctx = c.getContext("2d");
      if (!sctx) return c;

      // Draw in CSS pixels while the bitmap is sized for DPR.
      sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const cx = q;
      const cy = q;
      const tau = TAU;

      // Outer halo
      const glowR = q * 2.05;
      const glow = sctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
      glow.addColorStop(0, `rgba(${p.r}, ${p.g}, ${p.b}, 0.03)`);
      glow.addColorStop(1, "rgba(0,0,0,0)");
      sctx.fillStyle = glow;
      sctx.beginPath();
      sctx.arc(cx, cy, glowR, 0, tau);
      sctx.fill();

      // Bead body
      const grad = sctx.createRadialGradient(cx - q * 0.35, cy - q * 0.35, 0, cx, cy, q);
      grad.addColorStop(0, "rgba(255,255,255,0.26)");
      grad.addColorStop(0.5, `rgba(${p.r}, ${p.g}, ${p.b}, 0.72)`);
      grad.addColorStop(1, `rgba(${p.darkR}, ${p.darkG}, ${p.darkB}, 0.66)`);
      sctx.fillStyle = grad;
      sctx.beginPath();
      sctx.arc(cx, cy, q, 0, tau);
      sctx.fill();

      // Specular highlight
      if (q >= 1.05) {
        const hx = cx - q * 0.35;
        const hy = cy - q * 0.35;
        const highlight = sctx.createRadialGradient(hx, hy, 0, hx, hy, q * 0.7);
        highlight.addColorStop(0, "rgba(255,255,255,0.12)");
        highlight.addColorStop(1, "rgba(255,255,255,0)");
        sctx.fillStyle = highlight;
        sctx.beginPath();
        sctx.arc(hx, hy, q * 0.85, 0, tau);
        sctx.fill();
      }

      beadSpriteCache.set(key, c);
      return c;
    };

    const drawSolidWord = (text: string, opacity: number) => {
      const { anchorX, anchorY } = contentRectRef.current;
      const fontSize = fontSizeRef.current || 64;
      if (!text || opacity <= 0.001) return;

      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = clamp(opacity, 0, 1);
      ctx.font = `600 ${Math.floor(fontSize)}px Montserrat, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Vibrant teal glow matching Join Beta button
      ctx.shadowColor = "rgba(109, 216, 208, 0.8)"; // --color-accent-light #6dd8d0
      ctx.shadowBlur = Math.max(18, fontSize * 0.4);
      ctx.fillStyle = "#5bc9c1"; // --color-accent - solid vibrant teal
      ctx.fillText(text, anchorX, anchorY);

      // Second pass for extra vibrancy
      ctx.shadowColor = "rgba(0, 212, 170, 0.6)"; // --color-accent-pure glow
      ctx.shadowBlur = Math.max(12, fontSize * 0.25);
      ctx.fillStyle = "#6dd8d0"; // --color-accent-light - brighter teal
      ctx.fillText(text, anchorX, anchorY);
      ctx.restore();
    };

    const drawBead = (p: Particle, alphaOverride?: number) => {
      const { width, height } = dimensionsRef.current;
      const alpha =
        (alphaOverride ?? p.baseAlpha) *
        (p.targetAlpha ?? 1) *
        vignetteAlpha(p.x, p.y, width, height) *
        (0.50 + 0.25 * p.z);
      if (alpha <= 0.001) return;

      ctx.globalCompositeOperation = "source-over";
      const radius = p.size * (0.86 + p.z * 0.28);
      const sprite = getBeadSprite(radius, p);

      const prevAlpha = ctx.globalAlpha;
      ctx.globalAlpha = alpha * 0.78;
      ctx.drawImage(sprite, p.x - radius, p.y - radius, radius * 2, radius * 2);
      ctx.globalAlpha = prevAlpha;
    };

    const stepFloat = (p: Particle, width: number, height: number, dt: number) => {
      const now = performance.now() / 1000;
      const isMobile = width < 520;
      const wobbleScale = isMobile ? 0.62 : 1;
      const wobbleX =
        Math.sin(now * (0.55 + p.wobbleSpeed) + p.wobblePhase) * 33 * wobbleScale;
      const wobbleY =
        Math.cos(now * (0.48 + p.wobbleSpeed * 0.9) + p.wobblePhase * 1.1) *
        27 *
        wobbleScale;

      const driftScale = isMobile ? 0.7 : 1;
      const driftX =
        Math.sin(now * (0.18 + p.wobbleSpeed * 0.05) + p.wobblePhase) * 3.2 * driftScale;
      const driftY =
        Math.cos(now * (0.16 + p.wobbleSpeed * 0.06) + p.wobblePhase * 1.3) *
        3.2 *
        driftScale;
      p.vx += driftX * dt;
      p.vy += driftY * dt;

      p.x += (p.vx + wobbleX) * dt;
      p.y += (p.vy + wobbleY) * dt;

      if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) {
        const pt = sampleFieldPoint(width, height);
        p.x = pt.x;
        p.y = pt.y;
      }

      // Soft containment: keeps motion continuous (no teleporting) while avoiding hard edges.
      const cx = width * 0.5;
      const cy = height * 0.5;
      const rx = width * 0.58;
      const ry = height * 0.42;
      const dx = p.x - cx;
      const dy = p.y - cy;
      const nx0 = dx / Math.max(1, rx * rx);
      const ny0 = dy / Math.max(1, ry * ry);
      const nl = Math.hypot(nx0, ny0) || 1;
      const nx = nx0 / nl;
      const ny = ny0 / nl;
      const rNorm = Math.sqrt((dx * dx) / Math.max(1, rx * rx) + (dy * dy) / Math.max(1, ry * ry));
      if (rNorm > 1) {
        const k = width < 520 ? 140 : 110;
        const pull = (rNorm - 1) * k;
        p.vx -= nx * pull * dt;
        p.vy -= ny * pull * dt;
        p.vx *= 0.988;
        p.vy *= 0.988;
      }

      const maxSpeed = width < 520 ? 18 : 22;
      const v = Math.hypot(p.vx, p.vy);
      if (v > maxSpeed) {
        const s = maxSpeed / v;
        p.vx *= s;
        p.vy *= s;
      }
    };

    const animate = () => {
      const { width, height } = dimensionsRef.current;
      const dpr = dprRef.current || 1;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const now = performance.now();
      const dt = clamp((now - lastFrameRef.current) / 1000, 0, 0.04);
      lastFrameRef.current = now;
      const elapsed = (now - phaseStartRef.current) / 1000;
      const particles = particlesRef.current;
      const textIndices = textParticleIndicesRef.current ?? [];

      const phase = phaseRef.current;

      // Timing knobs: slower + symmetrical.
      const isMobile = width < 520;
      const floatSeconds = isMobile ? 1.55 : 1.05;
      const formSeconds = isMobile ? 2.15 : 1.65;
      const displaySeconds = isMobile ? 3.7 : 3.0;
      const morphSeconds = isMobile ? 1.45 : 1.15;

      if (phase === "float") {
        particles.forEach((p) => {
          stepFloat(p, width, height, dt);
          p.size = p.baseSize * (0.95 + Math.sin(now / 1200 + p.wobblePhase) * 0.08);
          p.targetAlpha = p.targetAlpha ?? p.baseAlpha;
          drawBead(p, p.baseAlpha);
        });

        if (elapsed > floatSeconds) {
          shownWordRef.current = WORDS[wordIndexRef.current];
          assignTargetsForWord(shownWordRef.current, "toText");
          phaseRef.current = "forming";
          phaseStartRef.current = now;
        }
      } else if (phase === "forming") {
        const t = clamp(elapsed / formSeconds, 0, 1);
        const eased = easeInOutCubic(t);

        particles.forEach((p, idx) => {
          if (p.mode === "toText") {
            if (p.delay > 0) {
              p.delay -= dt;
              stepFloat(p, width, height, dt);
              drawBead(p, p.baseAlpha * 0.9);
              return;
            }
            p.progress = Math.min(1, p.progress + dt / formSeconds);
            const e = easeInOutCubic(p.progress);
            p.x = p.startX + (p.targetX - p.startX) * e;
            p.y = p.startY + (p.targetY - p.startY) * e;
            p.size = p.targetSize * (0.98 + Math.sin(now / 1400 + p.wobblePhase) * 0.04);
            p.baseAlpha = Math.min(1, p.baseAlpha + dt * 1.25);
            drawBead(p, 1);
            if (p.progress >= 1) p.mode = "text";
          } else {
            stepFloat(p, width, height, dt);
            drawBead(p, p.baseAlpha * (0.55 - 0.25 * eased));
          }
        });

        if (t >= 1) {
          phaseRef.current = "display";
          phaseStartRef.current = now;
        }
      } else if (phase === "display") {
        const wobbleScale = isMobile ? 0.6 : 1;
        particles.forEach((p, idx) => {
          if (p.mode === "text") {
            const wobbleX = Math.sin(now / 880 + p.wobblePhase) * 0.55 * wobbleScale;
            const wobbleY = Math.cos(now / 1040 + p.wobblePhase) * 0.35 * wobbleScale;
            p.x = p.targetX + wobbleX;
            p.y = p.targetY + wobbleY;
            p.size = p.targetSize * (0.98 + Math.sin(now / 1500 + p.wobblePhase) * 0.03);
            drawBead(p, isMobile ? 0.045 : 0.06);
          } else {
            stepFloat(p, width, height, dt);
            drawBead(p, p.baseAlpha * (isMobile ? 0.18 : 0.22));
          }
        });

        drawSolidWord(shownWordRef.current, 1);

        if (elapsed > displaySeconds) {
          const nextIndex = (wordIndexRef.current + 1) % WORDS.length;
          incomingWordRef.current = WORDS[nextIndex];
          assignTargetsForWord(incomingWordRef.current, "morph");
          phaseRef.current = "morph";
          phaseStartRef.current = now;
        }
      } else if (phase === "morph") {
        const t = clamp(elapsed / morphSeconds, 0, 1);
        const eased = easeInOutCubic(t);
        const wobbleScale = isMobile ? 0.6 : 1;

        // Fade out old solid word, then fade in the new one (symmetrical feel).
        const fadeOut = clamp(1 - elapsed / (morphSeconds * 0.35), 0, 1);
        const fadeIn = clamp((elapsed - morphSeconds * 0.65) / (morphSeconds * 0.35), 0, 1);
        const showIncoming = elapsed >= morphSeconds * 0.5;
        const solidText = showIncoming ? incomingWordRef.current : shownWordRef.current;
        const solidOpacity = showIncoming ? fadeIn : fadeOut;

        particles.forEach((p) => {
          if (p.mode === "morph") {
            if (p.delay > 0) {
              p.delay -= dt;
              // Keep the text particles alive while waiting.
              const wobble = Math.sin(now / 820 + p.wobblePhase) * 0.6 * wobbleScale;
              p.x = p.startX + wobble;
              p.y = p.startY - wobble * 0.4;
              drawBead(p, isMobile ? 0.18 : 0.26);
              return;
            }
            p.progress = Math.min(1, p.progress + dt / morphSeconds);
            const e = easeInOutCubic(p.progress);
            p.x = p.startX + (p.targetX - p.startX) * e;
            p.y = p.startY + (p.targetY - p.startY) * e;
            p.size =
              (p.targetSize * 0.98) * (0.98 + Math.sin(now / 1400 + p.wobblePhase) * 0.03);
            drawBead(p, isMobile ? 0.42 : 0.58);
            if (p.progress >= 1) p.mode = "text";
          } else if (p.mode === "text") {
            const wobbleX = Math.sin(now / 880 + p.wobblePhase) * 0.55 * wobbleScale;
            const wobbleY = Math.cos(now / 1040 + p.wobblePhase) * 0.35 * wobbleScale;
            p.x = p.targetX + wobbleX;
            p.y = p.targetY + wobbleY;
            drawBead(p, isMobile ? 0.18 : 0.26);
          } else {
            stepFloat(p, width, height, dt);
            drawBead(p, p.baseAlpha * 0.32);
          }
        });

        if (solidText) drawSolidWord(solidText, solidOpacity);

        if (t >= 1) {
          wordIndexRef.current = (wordIndexRef.current + 1) % WORDS.length;
          shownWordRef.current = incomingWordRef.current ?? shownWordRef.current;
          incomingWordRef.current = null;
          phaseRef.current = "display";
          phaseStartRef.current = now;
        }
      }

      animationRef.current = window.requestAnimationFrame(animate);
    };

    animationRef.current = window.requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) window.cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <div className="animated-header" ref={containerRef}>
      <h1 className="header-line-1">
        <span className="hero-static">{HERO_HEADLINE.en.line1}</span>{" "}
        <span className="hero-lockup">
          <span className="midash-brand">{HERO_HEADLINE.en.brand}</span>
          {HERO_HEADLINE.en.line2 && <span className="hero-tail"> {HERO_HEADLINE.en.line2}</span>}
        </span>
      </h1>
      <div className="canvas-container" ref={canvasContainerRef}>
        <canvas ref={canvasRef} className="particle-canvas" />
      </div>
      <div className="hero-microline">All in one connected to your broker</div>
    </div>
  );
};

export default AnimatedHeaderMorph;
